import React from "react";
import { useState, useEffect, useRef } from "react";

// Format message text for better readability
function formatMessage(text) {
  if (!text) return text;

  // Split by double newlines for paragraphs
  const paragraphs = text.split(/\n\n+/);

  return paragraphs.map((para, pIdx) => {
    // Handle bullet points (lines starting with -, *, or •)
    if (para.match(/^[\s]*[-*•]\s/m)) {
      const lines = para.split(/\n/);
      return (
        <div key={pIdx} style={{ marginBottom: pIdx < paragraphs.length - 1 ? "8px" : "0" }}>
          {lines.map((line, lIdx) => {
            if (line.match(/^[\s]*[-*•]\s/)) {
              return (
                <div key={lIdx} style={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: "4px",
                  paddingLeft: "4px"
                }}>
                  <span style={{ marginRight: "8px", color: "#6b7280" }}>•</span>
                  <span style={{ flex: 1 }}>{line.replace(/^[\s]*[-*•]\s/, "")}</span>
                </div>
              );
            }
            return line.trim() ? (
              <div key={lIdx} style={{ marginBottom: "4px" }}>{line}</div>
            ) : null;
          })}
        </div>
      );
    }

    // Handle numbered lists
    if (para.match(/^\d+\.\s/m)) {
      const lines = para.split(/\n/);
      return (
        <div key={pIdx} style={{ marginBottom: pIdx < paragraphs.length - 1 ? "8px" : "0" }}>
          {lines.map((line, lIdx) => {
            const match = line.match(/^(\d+)\.\s(.+)$/);
            if (match) {
              return (
                <div key={lIdx} style={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: "4px"
                }}>
                  <span style={{ marginRight: "8px", color: "#6b7280", fontWeight: "500" }}>
                    {match[1]}.
                  </span>
                  <span style={{ flex: 1 }}>{match[2]}</span>
                </div>
              );
            }
            return line.trim() ? (
              <div key={lIdx} style={{ marginBottom: "4px" }}>{line}</div>
            ) : null;
          })}
        </div>
      );
    }

    // Regular paragraph - preserve single line breaks
    const formattedPara = para.split(/\n/).map((line, lIdx, arr) => (
      <React.Fragment key={lIdx}>
        {line}
        {lIdx < arr.length - 1 && <br />}
      </React.Fragment>
    ));

    return (
      <div key={pIdx} style={{ marginBottom: pIdx < paragraphs.length - 1 ? "8px" : "0" }}>
        {formattedPara}
      </div>
    );
  });
}

export default function ResumeChatWidget({ resume }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Keep width fixed for vertical expansion; only height will be changed by the resize handle
  const [panelSize, setPanelSize] = useState(() => ({ width: 320, height: Math.round(window.innerHeight * 0.6) }));
  const resizingRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0, w: 0, h: 0 });

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      resizingRef.current = false;
      document.body.style.cursor = '';
    };
  }, []);

  // Vertical-only resize (drag up/down from the bottom center)
  const startResize = (e) => {
    e.preventDefault();
    startRef.current = { x: e.clientX, y: e.clientY, w: panelSize.width, h: panelSize.height };

    const handleMouseMove = (ev) => {
      const dy = ev.clientY - startRef.current.y;
      const maxH = Math.round(window.innerHeight - 40);
      const newH = Math.min(maxH, Math.max(200, startRef.current.h + dy));
      setPanelSize((prev) => ({ ...prev, height: newH }));
    };

    const handleMouseUp = () => {
      resizingRef.current = false;
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    resizingRef.current = true;
    // use a vertical resize cursor
    document.body.style.cursor = 'ns-resize';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Focus input when chat opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  async function sendMessage() {
    const messageText = input.trim();
    if (!messageText || loading) return;

    const userMessage = { role: "user", text: messageText };
    setMessages((m) => [...m, userMessage]);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("https://my-mongodb-api-pied.vercel.app/api/chat?type=chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: messageText, resume })
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      let fullText = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        fullText += chunk;
      }

      if (!res.ok) {
        throw new Error(`Failed to get response: ${res.status}`);
      }

      setMessages((m) => [...m, { role: "assistant", text: fullText || "I'm sorry, I couldn't process that request." }]);
      // 2. The gtag event call
      if (window?.gtag) {
        window.gtag('event', 'button_click', {
          'event_category': 'chat',
          'event_label': 'resume coach'
        });
      }
    } catch (err) {
      setError("Failed to send message. Please try again.");
      setMessages((m) => [...m, {
        role: "assistant",
        text: "Sorry, I encountered an error. Please try again later."
      }]);
    } finally {
      setLoading(false);
      // reset textarea height after sending
      if (inputRef.current) {
        try { inputRef.current.style.height = '40px'; } catch (e) { /* ignore */ }
      }
    }
  }

  // compute panel style
  const panelBase = {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    width: "320px",
    maxWidth: "calc(100vw - 40px)",
    height: "420px",
    maxHeight: "calc(100vh - 40px)",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    zIndex: 1000,
  };

  // When expanded we keep the panel width fixed (same as base) and only apply the expanded height
  // Ensure the expanded height never exceeds the viewport (leave 20px margin top/bottom)
  const maxViewportHeight = typeof window !== 'undefined' ? Math.round(window.innerHeight - 40) : 800;
  const panelExpanded = {
    width: panelBase.width,
    height: Math.min(panelSize.height, maxViewportHeight) + 'px',
    right: "20px",
    bottom: "20px",
    borderRadius: "12px",
    boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
    zIndex: 2000,
  };

  const panelStyle = expanded
    ? { ...panelBase, ...panelExpanded, transform: "translateY(0) scale(1)", opacity: 1, pointerEvents: "auto" }
    : { ...panelBase, transform: open ? "translateY(0) scale(1)" : "translateY(50px) scale(0.95)", opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none" };

  return (
    <div className="resume-chat-widget">
      {/* Floating bubble button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open Resume Coach chat"
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            backgroundColor: "#111",
            color: "#fff",
            border: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            fontSize: "24px",
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            transition: "all 0.2s ease",
            zIndex: 999,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
          }}
        >
          💬
        </button>
      )}

      {/* Chat panel */}
      <div style={panelStyle}>
        {/* Header */}
        <div
          style={{
            backgroundColor: "#111",
            color: "#fff",
            padding: "10px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: "600",
            fontSize: "15px",
            minHeight: "48px",
          }}
        >
          <span>💬 Resume Coach</span>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => {
                setExpanded((e) => {
                  const next = !e;
                  if (next) {
                    // expand to full allowed viewport height (leave 20px margin top/bottom)
                    const maxH = Math.round(window.innerHeight - 40);
                    setPanelSize((ps) => ({ ...ps, height: maxH }));
                  }
                  return next;
                });
              }}
              aria-label={expanded ? "Collapse chat" : "Expand chat"}
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                fontSize: "18px",
                width: "36px",
                height: "36px",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: 8,
              }}
            >
              {expanded ? '⤡' : '⤢'}
            </button>
            <button
              onClick={() => { setOpen(false); setExpanded(false); }}
              aria-label="Close chat"
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                fontSize: "20px",
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                fontWeight: "300",
                lineHeight: "1",
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Messages */}
        <div
          role="log"
          aria-live="polite"
          style={{
            flex: 1,
            padding: "12px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            backgroundColor: "#f9fafb",
          }}
        >
          {messages.length === 0 && !loading && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                color: "#6b7280",
                fontSize: "14px",
                textAlign: "center",
                padding: "20px",
              }}
            >
              <div style={{ fontSize: "32px", marginBottom: "8px" }}>👋</div>
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>Welcome!</div>
              <div>Ask me anything about your resume</div>
            </div>
          )}

          {messages.map((m, i) => (
            <div
              key={i}
              style={{
                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                animation: "fadeInMessage 0.3s ease-in",
                maxWidth: "90%",
              }}
            >
              <div
                style={{
                  display: "inline-block",
                  padding: m.role === "assistant" ? "12px 14px" : "8px 12px",
                  borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                  backgroundColor: m.role === "user" ? "#2563EB" : "#ffffff",
                  color: m.role === "user" ? "#fff" : "#374151",
                  wordWrap: "break-word",
                  fontSize: m.role === "assistant" ? "14px" : "14px",
                  lineHeight: m.role === "assistant" ? "1.6" : "1.5",
                  boxShadow: m.role === "user"
                    ? "0 1px 2px rgba(0,0,0,0.1)"
                    : "0 1px 3px rgba(0,0,0,0.08)",
                  border: m.role === "assistant" ? "1px solid #e5e7eb" : "none",
                  fontFamily: m.role === "assistant" ? "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" : "inherit",
                }}
              >
                {m.role === "assistant" ? formatMessage(m.text) : m.text}
              </div>
            </div>
          ))}

          {/* Typing dots */}
          {loading && (
            <div
              style={{
                display: "flex",
                gap: "3px",
                padding: "2px 4px",
                alignSelf: "flex-start",
                backgroundColor: "#ffffff",
                borderRadius: "6px 6px 6px 4px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <span style={dotStyle(0)}></span>
              <span style={dotStyle(150)}></span>
              <span style={dotStyle(300)}></span>
            </div>
          )}

          {error && (
            <div
              style={{
                padding: "8px 12px",
                backgroundColor: "#fee2e2",
                color: "#991b1b",
                borderRadius: "8px",
                fontSize: "13px",
                border: "1px solid #fecaca",
              }}
            >
              {error}
            </div>
          )}

          <div ref={messagesEndRef}></div>
        </div>

        {/* Input */}
        <div
          style={{
            display: "flex",
            padding: "12px",
            borderTop: "1px solid #e5e7eb",
            gap: "8px",
            backgroundColor: "#fff",
          }}
        >
          {/* Auto-resizing textarea for multi-line input */}
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              // auto-resize
              try {
                const ta = e.target;
                ta.style.height = 'auto';
                const maxH = Math.round(window.innerHeight * 0.4); // limit to 40% of viewport
                const newH = Math.min(maxH, ta.scrollHeight);
                ta.style.height = newH + 'px';
              } catch (err) { /* ignore */ }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !loading && input.trim()) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Ask about your resume…"
            disabled={loading}
            rows={1}
            style={{
              flex: 1,
              padding: "9px 12px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              outline: "none",
              fontSize: "14px",
              transition: "all 0.2s ease",
              backgroundColor: loading ? "#f9fafb" : "#fff",
              color: loading ? "#9ca3af" : "#111",
              resize: 'none',
              overflow: 'hidden',
              minHeight: '40px',
              maxHeight: Math.round(window.innerHeight * 0.4) + 'px'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "#2563EB";
              e.currentTarget.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
              // ensure size matches content on focus
              try {
                const ta = e.currentTarget;
                ta.style.height = 'auto';
                const maxH = Math.round(window.innerHeight * 0.4);
                ta.style.height = Math.min(maxH, ta.scrollHeight) + 'px';
              } catch (err) {}
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "#d1d5db";
              e.currentTarget.style.boxShadow = "none";
            }}
          />
          <button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            style={{
              backgroundColor: loading || !input.trim() ? "#d1d5db" : "#111",
              color: "#fff",
              border: "none",
              padding: "6px 10px",
              borderRadius: "8px",
              cursor: loading || !input.trim() ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1,
              fontSize: "13px",
              whiteSpace: "nowrap",
              transition: "all 0.2s ease",
              fontWeight: "500",
            }}
            onMouseEnter={(e) => {
              if (!loading && input.trim()) {
                e.currentTarget.style.backgroundColor = "#1f2937";
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && input.trim()) {
                e.currentTarget.style.backgroundColor = "#111";
              }
            }}
          >
            {loading ? "..." : "Send"}
          </button>
        </div>

        {/* Resize handle - visible when expanded; bottom-centered for vertical resizing */}
        {expanded && (
          <div
            onMouseDown={startResize}
            title="Drag to resize vertically"
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 8,
              width: 120,
              height: 10,
              borderRadius: 6,
              background: 'rgba(0,0,0,0.06)',
              cursor: 'ns-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 3000,
            }}
          >
            <div style={{ width: 48, height: 4, borderRadius: 2, background: 'rgba(0,0,0,0.15)' }} />
          </div>
        )}
      </div>
    </div>
  );
}

// Animated typing dots
const dotStyle = (delay = 0) => ({
  width: "1px",
  height: "1px",
  borderRadius: "10%",
  backgroundColor: "#111",
  display: "inline-block",
  animation: `bounce 1.4s infinite ease-in-out`,
  animationDelay: `${delay}ms`,
});

// Add CSS animations globally
if (typeof document !== "undefined") {
  const styleId = "resume-chat-widget-styles";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      @keyframes bounce {
        0%, 80%, 100% { 
          transform: scale(0.8);
          opacity: 0.5;
        }
        40% { 
          transform: scale(1);
          opacity: 1;
        }
      }
      @keyframes fadeInMessage {
        from {
          opacity: 0;
          transform: translateY(4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      /* Custom scrollbar for messages */
      [role="log"]::-webkit-scrollbar {
        width: 6px;
      }
      [role="log"]::-webkit-scrollbar-track {
        background: transparent;
      }
      [role="log"]::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 3px;
      }
      [role="log"]::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
      }
      /* Better text rendering for chat messages */
      [role="log"] {
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
    `;
    document.head.appendChild(style);
  }
}
